import { AnalyticsUserEvent } from '@fruitful/types';
import { Redirect, router } from 'expo-router';
import { useEffect } from 'react';
import { FullScreenSpinner } from 'smoothie';

import { IncomeSplitReviewNew } from '../../../../banking/components/income-split/IncomeSplitReviewNew';
import { useInternalInvestmentAccountsForIncomeSplit } from '../../../../banking/hooks/income-split/useInternalInvestmentAccountsForIncomeSplit';
import useGetAllBankAccountDetails from '../../../../banking/hooks/useGetAllBankAccountDetails';
import { useViewEvents } from '../../../../events/hooks/useViewEvents';
import { captureException, track } from '../../../../logger/Logger';
import useGetProposedIncomeSplit from '../../../hooks/useGetProposedIncomeSplit';
import { useMoneyMapWorkflow } from '../../../hooks/useMoneyMapWorkflow';
import { GoWorkflowStepType } from '../../../types/GoWorkflow';

const ProposedIncomeSplit = () => {
    const { trackViewOnce } = useViewEvents();

    const {
        internalInvestmentAccounts,
        isLoading: isLoadingInternalInvestmentAccounts,
    } = useInternalInvestmentAccountsForIncomeSplit();

    const {
        isLoadingMoneyMap,
        moneyMap,
        hasMoneyMapError,
        moneyMapCompleteness,
        refetchMoneyMapCompleteness,
        trackWorkflowCompletedEvent,
    } = useMoneyMapWorkflow();

    if (
        !isLoadingMoneyMap &&
        !moneyMapCompleteness.incomeSplitStep.isIncluded
    ) {
        router.navigate('/home/roadmap');
    }

    const {
        isLoading: isLoadingIncomeSplit,
        data: incomeSplit,
        isError: isIncomeSplitError,
        error: incomeSplitError,
    } = useGetProposedIncomeSplit(moneyMap);

    const { bankAccountDetails, isLoading: isLoadingBankAccountDetails } =
        useGetAllBankAccountDetails();

    const isLoading =
        isLoadingMoneyMap ||
        isLoadingIncomeSplit ||
        isLoadingBankAccountDetails ||
        isLoadingInternalInvestmentAccounts;

    const hasError = hasMoneyMapError || isIncomeSplitError;

    useEffect(() => {
        if (hasError) {
            incomeSplitError && captureException(incomeSplitError);
            router.navigate('/home/roadmap');
        }
    }, [hasError, incomeSplitError]);

    if (!isLoading && (!moneyMap || !incomeSplit)) {
        return <Redirect href="/home/roadmap" />;
    }

    if (isLoading || !moneyMap || !incomeSplit) {
        return <FullScreenSpinner />;
    }

    trackViewOnce('Roadmap/Go/IncomeSplit');

    const incomeSplitComplete =
        moneyMapCompleteness.incomeSplitStep.isCompleteForMember;

    return (
        <IncomeSplitReviewNew
            incomeSplit={incomeSplit}
            bankAccountDetails={bankAccountDetails}
            internalInvestmentAccounts={internalInvestmentAccounts}
            context="goButtonFlow"
            incomeSplitComplete={incomeSplitComplete}
            onSubmit={() => {
                track(
                    AnalyticsUserEvent.MONEY_MAP_WORKFLOW_STEP_SUBMIT_INCOME_SPLIT,
                );
            }}
            onIncomeSplitCreated={async () => {
                track(
                    AnalyticsUserEvent.MONEY_MAP_WORKFLOW_INCOME_SPLIT_COMPLETE,
                );
                await refetchMoneyMapCompleteness();
                trackWorkflowCompletedEvent(GoWorkflowStepType.INCOME_SPLIT);
            }}
            moneyMap={moneyMap}
        />
    );
};

export default ProposedIncomeSplit;
